import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import membershipService, { CreatePlanDto, AssignMembershipDto } from '../../services/membershipService';
import { MembershipPlan, ClientMembership, MembershipStats } from '../../types';

interface MembershipState {
  plans:    MembershipPlan[];
  members:  ClientMembership[];
  stats:    MembershipStats | null;
  total:    number;
  loading:  boolean;
  error:    string | null;
}

const initialState: MembershipState = {
  plans: [], members: [], stats: null, total: 0, loading: false, error: null,
};

const upsertPlan = (list: MembershipPlan[], p: MembershipPlan) => {
  const i = list.findIndex(x => x._id === p._id);
  if (i !== -1) list[i] = p; else list.unshift(p);
};
const upsertMember = (list: ClientMembership[], m: ClientMembership) => {
  const i = list.findIndex(x => x._id === m._id);
  if (i !== -1) list[i] = m; else list.unshift(m);
};

type ApiError = { response?: { data?: { message?: string } } };
const msg = (e: unknown) => (e as ApiError).response?.data?.message ?? 'Request failed';

export const fetchMembershipStats = createAsyncThunk('membership/stats',       async (_, { rejectWithValue }) => { try { return await membershipService.getStats(); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const fetchPlans           = createAsyncThunk('membership/fetchPlans',   async (_, { rejectWithValue }) => { try { return await membershipService.getPlans(); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const createPlan           = createAsyncThunk('membership/createPlan',   async (d: CreatePlanDto, { rejectWithValue }) => { try { return await membershipService.createPlan(d); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const updatePlan           = createAsyncThunk('membership/updatePlan',   async ({ id, data }: { id: string; data: Partial<CreatePlanDto> }, { rejectWithValue }) => { try { return await membershipService.updatePlan(id, data); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const deletePlan           = createAsyncThunk('membership/deletePlan',   async (id: string, { rejectWithValue }) => { try { await membershipService.deletePlan(id); return id; } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const fetchMembers         = createAsyncThunk('membership/fetchMembers', async (p: Parameters<typeof membershipService.getMembers>[0], { rejectWithValue }) => { try { return await membershipService.getMembers(p); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const assignMembership     = createAsyncThunk('membership/assign',       async (d: AssignMembershipDto, { rejectWithValue }) => { try { return await membershipService.assign(d); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const cancelMembership     = createAsyncThunk('membership/cancel',       async (id: string, { rejectWithValue }) => { try { return await membershipService.cancel(id); } catch (e: unknown) { return rejectWithValue(msg(e)); } });
export const renewMembership      = createAsyncThunk('membership/renew',        async ({ id, amountPaid }: { id: string; amountPaid?: number }, { rejectWithValue }) => { try { return await membershipService.renew(id, amountPaid); } catch (e: unknown) { return rejectWithValue(msg(e)); } });

const membershipSlice = createSlice({
  name: 'membership', initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembershipStats.fulfilled, (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchPlans.pending,    (s) => { s.loading = true; })
      .addCase(fetchPlans.fulfilled,  (s, a) => { s.loading = false; s.plans = a.payload.data ?? []; })
      .addCase(fetchPlans.rejected,   (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createPlan.fulfilled,  (s, a) => { if (a.payload.data) upsertPlan(s.plans, a.payload.data); })
      .addCase(updatePlan.fulfilled,  (s, a) => { if (a.payload.data) upsertPlan(s.plans, a.payload.data); })
      .addCase(deletePlan.fulfilled,  (s, a) => { const p = s.plans.find(p => p._id === a.payload); if (p) p.isActive = false; })
      .addCase(fetchMembers.pending,  (s) => { s.loading = true; })
      .addCase(fetchMembers.fulfilled,(s, a) => { s.loading = false; s.members = a.payload.data ?? []; s.total = a.payload.total ?? 0; })
      .addCase(fetchMembers.rejected, (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(assignMembership.fulfilled,  (s, a) => { if (a.payload.data) upsertMember(s.members, a.payload.data); })
      .addCase(cancelMembership.fulfilled,  (s, a) => { if (a.payload.data) upsertMember(s.members, a.payload.data); })
      .addCase(renewMembership.fulfilled,   (s, a) => { if (a.payload.data) upsertMember(s.members, a.payload.data); });
  },
});

export const { clearError } = membershipSlice.actions;
export default membershipSlice.reducer;
